/**
 * API Client
 * Matches main app's API client pattern
 * Simplified with native fetch instead of axios for smaller bundle
 */

import type { ChatParticipant, ChatRequest, ChatResponse, EmbeddedComponent } from '../types/chat';
import { t } from '@/lib/i18n';
import { getProviderId } from '@/lib/runtimeConfig';

export interface AuthMeResponse {
  tenant: { id: string; name?: string };
  user: { id: string; email?: string; displayName?: string };
  roles: string[];
  permissions: string[];
}

export interface ConversationHistoryMessage {
  messageId: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  senderId?: string;
  senderName?: string;
  senderType?: 'customer' | 'staff' | 'ai_agent';
  embeddedComponent?: EmbeddedComponent;
}

export type EducationScopeType = 'general' | 'class_section' | 'student' | 'admissions_application' | 'family_request';
export type EducationAudienceType = 'private_ai' | 'class_staff' | 'student_staff' | 'admissions_staff';

export interface EducationDestination {
  type: EducationScopeType;
  id: string;
  label: string;
  status?: string;
  student_count?: number;
}

export interface EducationConversation {
  conversation_id: string;
  title: string;
  scope: { type: EducationScopeType; id: string | null; label: string };
  source_refs: Array<{ kind: string; id: string }>;
  education_facts: Record<string, unknown>;
  audience_type: EducationAudienceType;
  ai_mode: 'on' | 'mentioned' | 'off';
  last_activity_at: string;
  unread_count: number;
}

const DEVICE_ID_STORAGE_KEY = 'xedu_device_id_v1';

function readEnv(key: string): string | undefined {
  try {
    const anyGlobal = globalThis as any;
    const value = anyGlobal?.process?.env?.[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  } catch {
    // ignore
  }

  return undefined;
}

function generateUuidV4(): string {
  // Prefer browser native UUID.
  if (typeof globalThis !== 'undefined') {
    const anyGlobal = globalThis as any;
    if (anyGlobal.crypto?.randomUUID) {
      return anyGlobal.crypto.randomUUID();
    }
  }

  // Fallback v4 generator.
  const hex = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, '0'));
  const rnd = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) {
    rnd[i] = Math.floor(Math.random() * 256);
  }
  rnd[6] = (rnd[6] & 0x0f) | 0x40;
  rnd[8] = (rnd[8] & 0x3f) | 0x80;

  return (
    hex[rnd[0]] +
    hex[rnd[1]] +
    hex[rnd[2]] +
    hex[rnd[3]] +
    '-' +
    hex[rnd[4]] +
    hex[rnd[5]] +
    '-' +
    hex[rnd[6]] +
    hex[rnd[7]] +
    '-' +
    hex[rnd[8]] +
    hex[rnd[9]] +
    '-' +
    hex[rnd[10]] +
    hex[rnd[11]] +
    hex[rnd[12]] +
    hex[rnd[13]] +
    hex[rnd[14]] +
    hex[rnd[15]]
  );
}

function getOrCreateDeviceId(): string {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const existing = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY);
      if (existing && existing.trim()) {
        return existing;
      }
      const created = generateUuidV4();
      window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, created);
      return created;
    }
  } catch {
    // Ignore storage errors and fall back to ephemeral ID.
  }
  return generateUuidV4();
}

interface ApiConfig {
  baseURL: string;
  timeout?: number;
  profile?: 'default' | 'adhd_external';
  sector?: string;
  headers?: Record<string, string>;
}

class ApiHttpError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly url: string;
  readonly responseText?: string;

  constructor(args: { status: number; statusText: string; url: string; responseText?: string }) {
    super(`HTTP ${args.status}: ${args.statusText}`);
    this.name = 'ApiHttpError';
    this.status = args.status;
    this.statusText = args.statusText;
    this.url = args.url;
    this.responseText = args.responseText;
  }
}

export function describeEducationChatError(error: unknown): string {
  const status = (error as { status?: number })?.status;
  if (status === 401) return 'Your school chat session needs refreshing. Reopen the assistant.';
  if (status === 403 || status === 404) return 'This record or conversation is no longer available to your account.';
  if (status === 503) return 'School records are temporarily unavailable. Please retry.';
  return error instanceof Error ? error.message : 'Unable to load school chat right now.';
}

class ChatApiClient {
  private config: ApiConfig;
  private readonly deviceId: string;
  private userContext: { user_type?: string; user_id?: string; roles?: string[] } | null = null;

  constructor(config: ApiConfig) {
    const envTimeoutRaw =
      readEnv('EXPO_PUBLIC_API_TIMEOUT_MS') ||
      readEnv('EXPO_PUBLIC_REQUEST_TIMEOUT_MS') ||
      readEnv('VITE_API_TIMEOUT_MS');

    const envTimeoutMs = envTimeoutRaw ? Number(envTimeoutRaw) : NaN;
    // Default slightly above backend's ~60s budget.
    // In practice a request can exceed 60s due to network + server-side overhead.
    const defaultTimeoutMs = Number.isFinite(envTimeoutMs) ? envTimeoutMs : 75000;

    this.deviceId = getOrCreateDeviceId();

    this.config = {
      timeout: defaultTimeoutMs,
      profile: 'default',
      ...config,
      headers: {
        ...(config.headers || {}),
        'X-Device-ID': this.deviceId,
        // Some integrations prefer sending a stable identifier via X-Request-ID.
        // We keep it aligned with X-Device-ID for session continuity.
        'X-Request-ID': this.deviceId,
      },
    };
  }

  private isAdhdExternalProfile(): boolean {
    return this.config.profile === 'adhd_external';
  }

  private async fetchJson<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.config.baseURL}${path}`;

    const headers = {
      'Content-Type': 'application/json',
      ...this.config.headers,
      ...options.headers,
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let responseText: string | undefined;
        try {
          responseText = (await response.text())?.slice(0, 5000);
        } catch {
          // ignore
        }

        throw new ApiHttpError({
          status: response.status,
          statusText: response.statusText,
          url,
          responseText,
        });
      }

      const contentType = response.headers.get('content-type') || '';
      // Prefer parsing based on content-type, but still guard against dev servers
      // returning HTML (which causes `Unexpected token '<'` errors).
      if (contentType.includes('application/json') || contentType.includes('application/problem+json')) {
        return (await response.json()) as T;
      }

      const text = await response.text();
      try {
        return JSON.parse(text) as T;
      } catch {
        const head = text.trim().slice(0, 80);
        if (head.startsWith('<!DOCTYPE') || head.startsWith('<html') || head.startsWith('<')) {
          throw new Error(
            t(
              'api.error.htmlInsteadOfJson',
              `API returned HTML instead of JSON. This usually means the Chat UI is pointing at the wrong server (baseURL='${this.config.baseURL || '(same-origin)'}'). Configure VITE_API_URL / EXPO_PUBLIC_API_URL to the backend (e.g. http://localhost:8007), or run behind a proxy that forwards /api/*.`
            )
          );
        }
        throw new Error(t('api.error.invalidJson', 'Invalid JSON response from API'));
      }
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error(t('api.error.timeout', 'Request timeout'));
        }
        throw error;
      }

      throw new Error(t('api.error.unknown', 'An unknown error occurred'));
    }
  }

  private async fetchChatJson<T>(
    chatPath: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Primary route: FastAPI router with `/api/chat/v2` prefix.
    try {
      return await this.fetchJson<T>(`/api/chat/v2${chatPath}`, options);
    } catch (error) {
      // Compatibility route: edge proxy strips `/api` prefix (server exposes `/chat/v2/*`).
      if (error instanceof ApiHttpError && error.status === 404) {
        return await this.fetchJson<T>(`/chat/v2${chatPath}`, options);
      }
      throw error;
    }
  }

  async getEducationNavigation(): Promise<{ sections: Array<{ key: string; label: string; items: EducationDestination[]; total: number }> }> {
    return this.fetchJson('/api/v1/education-chat/navigation', { method: 'GET' });
  }

  async getEducationClassStudents(sectionId: string): Promise<{ items: EducationDestination[]; total: number }> {
    return this.fetchJson(`/api/v1/education-chat/class-sections/${encodeURIComponent(sectionId)}/students`, { method: 'GET' });
  }

  async getEducationConversations(): Promise<{ conversations: EducationConversation[] }> {
    return this.fetchJson('/api/v1/education-chat/conversations', { method: 'GET' });
  }

  async createEducationConversation(scopeType: EducationScopeType, scopeId: string | null, audienceType: EducationAudienceType): Promise<EducationConversation> {
    return this.fetchJson('/api/v1/education-chat/conversations', {
      method: 'POST',
      body: JSON.stringify({ scope_type: scopeType, scope_id: scopeId, audience_type: audienceType }),
    });
  }

  async getEducationConversation(conversationId: string): Promise<EducationConversation> {
    return this.fetchJson(`/api/v1/education-chat/conversations/${encodeURIComponent(conversationId)}`, { method: 'GET' });
  }

  async postEducationStaffMessage(conversationId: string, content: string): Promise<void> {
    await this.fetchJson(`/api/v1/education-chat/conversations/${encodeURIComponent(conversationId)}/staff-messages`, {
      method: 'POST', body: JSON.stringify({ content, client_message_id: generateUuidV4() }),
    });
  }

  private async fetch<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Legacy API shape (not used by the FastAPI `/api/chat/` endpoint).
    const url = `${this.config.baseURL}${endpoint}`;
    
    const headers = {
      'Content-Type': 'application/json',
      ...this.config.headers,
      ...options.headers,
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json') || contentType.includes('application/problem+json')) {
        return (await response.json()) as T;
      }

      const text = await response.text();
      try {
        return JSON.parse(text) as T;
      } catch {
        const head = text.trim().slice(0, 80);
        if (head.startsWith('<!DOCTYPE') || head.startsWith('<html') || head.startsWith('<')) {
          throw new Error(
            t(
              'api.error.htmlInsteadOfJson',
              `API returned HTML instead of JSON. This usually means the Chat UI is pointing at the wrong server (baseURL='${this.config.baseURL || '(same-origin)'}'). Configure VITE_API_URL / EXPO_PUBLIC_API_URL to the backend (e.g. http://localhost:8007), or run behind a proxy that forwards /api/*.`
            )
          );
        }
        throw new Error(t('api.error.invalidJson', 'Invalid JSON response from API'));
      }
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error(t('api.error.timeout', 'Request timeout'));
        }
        throw error;
      }
      
      throw new Error(t('api.error.unknown', 'An unknown error occurred'));
    }
  }

  async sendMessage(request: ChatRequest): Promise<ChatResponse> {
    if (this.isAdhdExternalProfile()) {
      const providerId = (getProviderId() || '').trim();
      const body: any = {
        message: request.message,
        protocol: request.protocol || 'MESSAGE',
        conversation_id: request.conversationId,
        user_id: this.userContext?.user_id,
        provider_id: providerId || undefined,
        context:
          request.context && typeof request.context === 'object' && Object.keys(request.context).length > 0
            ? request.context
            : undefined,
      };

      const raw = await this.fetchJson<any>(`/api/external/adhd/chat`, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      return {
        message: raw.response ?? raw.message ?? '',
        conversationId: raw.conversation_id ?? raw.conversationId ?? '',
        timestamp: raw.timestamp ?? new Date().toISOString(),
        suggestions: raw.help_hints ?? raw.suggestions,
        topic: raw.intent,
        senderId: raw.sender_id ?? raw.senderId,
        senderName: raw.sender_name ?? raw.senderName,
      };
    }

    // Backend in this repo exposes `POST /api/chat/v2/` and uses snake_case.
    // Keep UI-facing types stable by adapting request/response here.
    const providerId = (getProviderId() || '').trim();
    
    // DON'T send sessionId as user_id for anonymous users
    // Backend will derive user_id from X-Device-ID header automatically
    // Only send user_id if we have a real authenticated user ID

    const body: any = {
      message: request.message,
      protocol: request.protocol,
      conversation_id: request.conversationId,
      // user_id is omitted for anonymous users - backend uses X-Device-ID instead
      provider_id: providerId || undefined,
      sector: request.sector || this.config.sector || 'xedu',
      context:
        request.context && typeof request.context === 'object' && Object.keys(request.context).length > 0
          ? request.context
          : undefined,
    };

    // Include user context if available (user_type, user_id, roles)
    if (this.userContext) {
      if (this.userContext.user_type) {
        body.user_type = this.userContext.user_type;
      }
      if (this.userContext.user_id) {
        body.user_id = this.userContext.user_id;
      }
      if (this.userContext.roles && this.userContext.roles.length > 0) {
        body.roles = this.userContext.roles;
      }
    }

    // Debug logging to verify what's being sent
    if (typeof console !== 'undefined' && console.log) {
      console.log('[ChatAPI] sendMessage body:', {
        has_user_context: !!this.userContext,
        user_type: body.user_type,
        user_id: body.user_id,
        roles: body.roles,
      });
    }

    const raw = await this.fetchChatJson<any>('/', {
      method: 'POST',
      body: JSON.stringify(body),
    });

    return {
      message: raw.response ?? raw.message ?? '',
      conversationId: raw.conversation_id ?? raw.conversationId ?? '',
      timestamp: raw.intent?.timestamp ?? new Date().toISOString(),
      embeddedComponent: raw.embeddedComponent ?? raw.embedded_component,
      uiAction: raw.ui_action ?? raw.uiAction,
      suggestions: raw.help_hints ?? raw.suggestions,
      topic: raw.topic,
      senderId: raw.sender_id ?? raw.senderId,
      senderName: raw.sender_name ?? raw.senderName,
    };
  }

  async getParticipants(conversationId: string): Promise<{ participants: ChatParticipant[] }> {
    if (this.isAdhdExternalProfile()) {
      return { participants: [] };
    }

    const raw = await this.fetchChatJson<any>(
      `/${encodeURIComponent(conversationId)}/participants`,
      { method: 'GET' }
    );

    const participants: ChatParticipant[] = Array.isArray(raw?.participants)
      ? raw.participants.map((p: any) => ({
          id: String(p.participant_id ?? p.participantId ?? ''),
          type: String(p.participant_type ?? p.participantType ?? 'customer') as any,
          displayName: String(p.display_name ?? p.displayName ?? ''),
          isHidden: Boolean(p.is_hidden ?? p.isHidden),
        }))
      : [];

    return { participants };
  }

  async getConversationMessages(
    conversationId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<{ conversationId: string; messages: ConversationHistoryMessage[]; totalCount: number }> {
    if (this.isAdhdExternalProfile()) {
      // ADHD external profile: fetch history from the dedicated ADHD history endpoint.
      const safeLimit = Math.min(Math.max(Number(limit) || 20, 1), 100);
      try {
        const raw = await this.fetchJson<any>(
          `/api/external/adhd/history/${encodeURIComponent(conversationId)}?limit=${safeLimit}&offset=${offset}`,
          { method: 'GET' }
        );

        const messages: ConversationHistoryMessage[] = Array.isArray(raw?.messages)
          ? raw.messages.map((m: any) => {
              const rawRole = String(m?.role ?? 'system').toLowerCase();
              const role: 'user' | 'assistant' | 'system' =
                rawRole === 'user' || rawRole === 'assistant' || rawRole === 'system'
                  ? rawRole
                  : 'system';
              return {
                messageId: String(m?.messageId ?? m?.message_id ?? ''),
                role,
                content: String(m?.content ?? ''),
                timestamp: String(m?.timestamp ?? new Date().toISOString()),
                embeddedComponent: m?.embeddedComponent ?? m?.embedded_component ?? undefined,
              };
            })
          : [];

        return {
          conversationId: String(raw?.conversation_id ?? conversationId),
          messages,
          totalCount: Number(raw?.total_count ?? messages.length) || 0,
        };
      } catch {
        // Non-fatal: if history fetch fails, fall back to fresh start.
        return { conversationId, messages: [], totalCount: 0 };
      }
    }

    const safeLimit = Math.min(Math.max(Number(limit) || 20, 1), 100);
    const raw = await this.fetchChatJson<any>(
      `/${encodeURIComponent(conversationId)}/messages?limit=${safeLimit}&offset=${offset}`,
      { method: 'GET' }
    );

    const messages: ConversationHistoryMessage[] = Array.isArray(raw?.messages)
      ? raw.messages.map((m: any) => {
          const rawRole = String(m?.role ?? 'system').toLowerCase();
          const role: 'user' | 'assistant' | 'system' =
            rawRole === 'user' || rawRole === 'assistant' || rawRole === 'system'
              ? rawRole
              : 'system';

          return {
            messageId: String(m?.message_id ?? m?.messageId ?? ''),
            role,
            content: String(m?.content ?? ''),
            timestamp: String(m?.timestamp ?? new Date().toISOString()),
            senderId: m?.sender_id ?? m?.senderId ?? undefined,
            senderName: m?.sender_name ?? m?.senderName ?? undefined,
            senderType:
              m?.sender_type === 'customer' || m?.sender_type === 'staff' || m?.sender_type === 'ai_agent'
                ? m.sender_type
                : m?.senderType === 'customer' || m?.senderType === 'staff' || m?.senderType === 'ai_agent'
                  ? m.senderType
                  : undefined,
            embeddedComponent: m?.embeddedComponent ?? m?.embedded_component ?? undefined,
          };
        })
      : [];

    return {
      conversationId: String(raw?.conversation_id ?? raw?.conversationId ?? conversationId),
      messages,
      totalCount: Number(raw?.total_count ?? raw?.totalCount ?? messages.length) || 0,
    };
  }

  async createInvite(conversationId: string): Promise<{ inviteLink: string; token: string }> {
    if (this.isAdhdExternalProfile()) {
      throw new Error(t('api.error.invitesUnsupported', 'Invite links are not supported for the ADHD external app.'));
    }

    const raw = await this.fetchChatJson<any>(
      `/${encodeURIComponent(conversationId)}/invites`,
      { method: 'POST' }
    );
    return {
      inviteLink: String(raw.invite_link ?? raw.inviteLink ?? ''),
      token: String(raw.token ?? ''),
    };
  }

  async acceptInvite(token: string, staffId?: string, displayName?: string): Promise<{ conversationId: string }> {
    if (this.isAdhdExternalProfile()) {
      throw new Error(t('api.error.invitesUnsupported', 'Invite links are not supported for the ADHD external app.'));
    }

    const raw = await this.fetchChatJson<any>(
      `/invites/accept`,
      {
        method: 'POST',
        body: JSON.stringify({
          token,
          staff_id: staffId,
          display_name: displayName,
        }),
      }
    );

    return {
      conversationId: String(raw.conversation_id ?? raw.conversationId ?? ''),
    };
  }

  async startConversation(): Promise<{ message: string; isReturning: boolean; conversationId?: string }>
  {
    if (this.isAdhdExternalProfile()) {
      const providerId = (getProviderId() || '').trim();
      const raw = await this.fetchJson<any>(`/api/external/adhd/chat`, {
        method: 'POST',
        body: JSON.stringify({
          protocol: 'INITIATE',
          message: 'I need help with ADHD information.',
          user_id: this.userContext?.user_id,
          provider_id: providerId || undefined,
        }),
      });

      return {
        message: raw.response ?? raw.message ?? '',
        isReturning: false,
        conversationId: raw.conversation_id ?? raw.conversationId ?? undefined,
      };
    }

    // IMPORTANT:
    // - The host (main app) is the source of truth for tenant/provider identity.
    // - During embed INIT, we set X-Tenant-ID via chatApi.setTenantId(...).
    // - Prefer that value for /api/chat/start so we don't send non-UUID tenant slugs.
    const isUuidLike = (value: string): boolean => {
      const s = (value || '').trim();
      if (!s) return false;
      return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
    };

    const headerTenantIdRaw = (this.config.headers?.['X-Tenant-ID'] || '').trim();
    const headerTenantId = isUuidLike(headerTenantIdRaw) ? headerTenantIdRaw : '';
    const providerIdRaw = (getProviderId() || '').trim();
    const providerId = headerTenantId || (isUuidLike(providerIdRaw) ? providerIdRaw : '');

    // Build query string with optional provider_id + user_type
    let queryString = '';
    if (providerId) {
      queryString = `provider_id=${encodeURIComponent(providerId)}`;
    }
    queryString += `${queryString ? '&' : ''}sector=${encodeURIComponent(this.config.sector || 'xedu')}`;
    if (this.userContext?.user_type) {
      queryString += `${queryString ? '&' : ''}user_type=${encodeURIComponent(this.userContext.user_type)}`;
    }

    // Debug logging
    if (typeof console !== 'undefined' && console.log) {
      console.log(`[ChatAPI ${Date.now()}] startConversation called with userContext:`, this.userContext);
      console.log(`[ChatAPI ${Date.now()}] startConversation query string:`, queryString);
    }

    const path = queryString ? `/start?${queryString}` : `/start`;
    const raw = await this.fetchChatJson<any>(path, { method: 'GET' });

    // Debug logging response
    if (typeof console !== 'undefined' && console.log) {
      console.log(`[ChatAPI ${Date.now()}] startConversation response:`, raw);
    }

    return {
      message: raw.message ?? '',
      isReturning: Boolean(raw.is_returning ?? raw.isReturning),
      conversationId: raw.conversation_id ?? raw.conversationId ?? undefined,
    };
  }

  async getAuthMe(): Promise<AuthMeResponse> {
    if (this.isAdhdExternalProfile()) {
      return {
        tenant: { id: '' },
        user: { id: this.userContext?.user_id || '' },
        roles: this.userContext?.roles || [],
        permissions: [],
      };
    }

    let raw: any;
    try {
      raw = await this.fetchJson<any>(`/auth/me`, { method: 'GET' });
    } catch (e) {
      // Backends may not expose /auth/me (older deployments), or the embed may
      // run unauthenticated. Treat these as non-fatal and continue.
      const anyErr = e as any;
      const status = typeof anyErr?.status === 'number' ? anyErr.status : undefined;
      if (status === 401 || status === 403 || status === 404) {
        return {
          tenant: { id: '', name: undefined },
          user: { id: '', email: undefined, displayName: undefined },
          roles: [],
          permissions: [],
        };
      }
      throw e;
    }

    return {
      tenant: {
        id: String(raw?.tenant?.id ?? raw?.tenant_id ?? ''),
        name: raw?.tenant?.name,
      },
      user: {
        id: String(raw?.user?.id ?? raw?.user_id ?? ''),
        email: raw?.user?.email,
        displayName: raw?.user?.displayName ?? raw?.user?.display_name,
      },
      roles: Array.isArray(raw?.roles) ? raw.roles.map(String) : [],
      permissions: Array.isArray(raw?.permissions) ? raw.permissions.map(String) : [],
    };
  }

  async getSuggestions(sessionId: string): Promise<{ suggestions: string[] }> {
    return this.fetch<{ suggestions: string[] }>(
      `/ai/suggestions?session_id=${sessionId}`,
      { method: 'GET' }
    );
  }

  async uploadFile(file: File): Promise<{ url: string; id: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${this.config.baseURL}/api/v1/ai/upload`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: this.config.headers,
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`${t('api.error.uploadFailed', 'Upload failed')}: ${response.statusText}`);
    }

    return response.json();
  }

  setAuthToken(token: string) {
    this.config.headers = {
      ...this.config.headers,
      Authorization: `Bearer ${token}`,
    };
  }

  clearAuthToken() {
    if (!this.config.headers?.Authorization) {
      return;
    }
    const { Authorization: _authorization, ...rest } = this.config.headers;
    this.config.headers = rest;
  }

  setTenantId(tenantId: string) {
    // Only propagate UUID-like tenant ids as headers.
    const s = (tenantId || '').trim();
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
    if (!isUuid) return;
    this.config.headers = {
      ...this.config.headers,
      'X-Tenant-ID': s,
    };
  }

  setUserContext(user: any) {
    const roles: string[] = Array.isArray(user?.roles) ? user.roles.map(String) : [];

    // Derive user_type from roles when the host doesn't supply it explicitly.
    // Priority: staff/admin > primary_user/participant/carer > non_staff.
    // This mirrors the server-side _map_roles_to_user_type() logic and prevents
    // silent staff→public_user fallback when user_type is absent from INIT payload.
    const _STAFF_ROLES = new Set(['staff', 'admin', 'tenant_admin', 'clinician', 'manager']);
    const _PRIMARY_ROLES = new Set(['primary_user', 'participant', 'carer']);
    const _deriveUserType = (r: string[]): string | undefined => {
      const lower = r.map((x) => x.trim().toLowerCase());
      if (lower.some((x) => _STAFF_ROLES.has(x))) return 'staff';
      if (lower.some((x) => _PRIMARY_ROLES.has(x))) return 'primary_user';
      if (lower.length > 0) return 'non_staff';
      return undefined;
    };

    const explicitUserType: string | undefined =
      user?.user_type || user?.userType || user?.type || undefined;
    const derivedUserType: string | undefined =
      explicitUserType || _deriveUserType(roles);
    const normalizedUserType =
      this.isAdhdExternalProfile() && !derivedUserType ? 'public_user' : derivedUserType;
    const normalizedRoles =
      roles.length > 0 ? roles : this.isAdhdExternalProfile() ? ['public_user'] : undefined;

    this.userContext = {
      user_type: normalizedUserType,
      user_id: user?.id || user?.user_id || user?.userId,
      roles: normalizedRoles,
    };

    // Debug logging to verify user context is set correctly
    if (typeof console !== 'undefined' && console.log) {
      console.log('[ChatAPI] setUserContext called with:', {
        input_user: user,
        extracted_context: this.userContext,
      });
    }
  }
}

// Singleton instance
let chatApiInstance: ChatApiClient | null = null;

export function initChatApi(config: ApiConfig): ChatApiClient {
  chatApiInstance = new ChatApiClient(config);
  return chatApiInstance;
}

export const chatApi = new Proxy({} as ChatApiClient, {
  get(_target, prop) {
    if (!chatApiInstance) {
      throw new Error(t('api.error.notInitialized', 'Chat API not initialized. Call initChatApi() first.'));
    }
    const value = (chatApiInstance as any)[prop];
    // Bind methods to the chatApiInstance to preserve 'this' context
    if (typeof value === 'function') {
      return value.bind(chatApiInstance);
    }
    return value;
  },
});
